import React from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { useKiosk } from '../../contexts/KioskContext'
import { useFamily } from '../../contexts/FamilyContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Button from '../ui/Button'
import Card from '../ui/Card'
import Badge from '../ui/Badge'

const { FiStar, FiLogOut, FiUsers } = FiIcons

const ChildSelectionScreen = ({ onChildSelected, onExitKiosk }) => {
  const { family } = useAuth()
  const { updateActivity } = useKiosk()
  const { getChildPoints, getChildLevel } = useFamily()

  const handleChildSelect = (child) => {
    updateActivity()
    onChildSelected(child)
  }

  if (!family?.children || family.children.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 flex items-center justify-center p-4">
        <Card className="text-center max-w-md">
          <div className="text-6xl mb-4">👨‍👩‍👧‍👦</div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
            No Children Added
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Ask your parents to add children to the family first.
          </p>
          <Button variant="outline" onClick={onExitKiosk}>
            <SafeIcon icon={FiLogOut} />
            Exit Kiosk Mode
          </Button>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-highlight-50 via-accent-50 to-primary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-700 relative overflow-hidden">
      {/* Floating background elements */}
      <div className="absolute inset-0 opacity-10 dark:opacity-5">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-4xl"
            initial={{
              x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1200),
              y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 800),
            }}
            animate={{
              y: [null, -30, 0],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 8 + Math.random() * 12,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 5
            }}
          >
            {['🌟', '⭐', '🎈', '🦄', '🌈', '✨', '🎊', '🎉'][Math.floor(Math.random() * 8)]}
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center shadow-lg">
              <SafeIcon icon={FiStar} className="text-white text-2xl" />
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                Chore Haven
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Kiosk Mode</p>
            </div>
          </motion.div>

          <Button 
            variant="ghost" 
            onClick={onExitKiosk}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          >
            <SafeIcon icon={FiLogOut} />
            Exit
          </Button>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="max-w-4xl w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <div className="text-8xl mb-6">👋</div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                Who's Ready for Chores?
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                Choose your profile to start completing tasks and earning points!
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {family.children.map((child, index) => {
                const points = getChildPoints(child.id)
                const level = getChildLevel(points)

                return (
                  <motion.div
                    key={child.id}
                    initial={{ opacity: 0, scale: 0.8, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Card 
                      className="cursor-pointer hover:shadow-2xl transition-all duration-300 p-8 text-center relative overflow-hidden bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                      onClick={() => handleChildSelect(child)}
                    >
                      {/* Glow effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-primary-100/20 via-transparent to-secondary-100/20 dark:from-primary-900/20 dark:via-transparent dark:to-secondary-900/20 opacity-0 hover:opacity-100 transition-opacity duration-300" />

                      {/* Level badge */}
                      <div className="absolute top-4 right-4">
                        <Badge variant="primary" className="text-xs font-bold">
                          Level {level.level}
                        </Badge>
                      </div>

                      <div className="relative z-10">
                        {/* Avatar */}
                        <motion.div
                          whileHover={{ rotate: [0, -10, 10, 0] }}
                          transition={{ duration: 0.5 }}
                          className="text-8xl mb-4"
                        >
                          {child.avatar || '👦'}
                        </motion.div>

                        {/* Name and Age */}
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                          {child.name}
                        </h3>
                        {child.age && (
                          <p className="text-gray-600 dark:text-gray-400 mb-4">Age {child.age}</p>
                        )}

                        {/* Level Title */}
                        <div className="mb-4">
                          <Badge variant="secondary" className="text-sm font-medium">
                            {level.title}
                          </Badge>
                        </div>

                        {/* Points */}
                        <div className="flex items-center justify-center gap-2 mb-4">
                          <SafeIcon icon={FiStar} className="text-yellow-500 dark:text-yellow-400 text-xl" />
                          <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                            {points}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">points</span>
                        </div>

                        {/* Progress to next level */}
                        <div className="mb-6">
                          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                            <span>Progress to Level {level.level + 1}</span>
                            <span>{level.next - points} more</span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-primary-400 to-secondary-400 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min((points / level.next) * 100, 100)}%` }}
                            />
                          </div>
                        </div>

                        {/* Call to action */}
                        <motion.div
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-full font-medium shadow-lg"
                        >
                          <SafeIcon icon={FiUsers} />
                          Let's Go!
                        </motion.div>
                      </div>

                      {/* Sparkle effects */}
                      <div className="absolute inset-0 pointer-events-none">
                        {[...Array(6)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="absolute w-1 h-1 bg-yellow-400 rounded-full"
                            style={{
                              left: `${20 + i * 15}%`,
                              top: `${20 + (i % 2) * 60}%`,
                            }}
                            animate={{
                              scale: [0, 1, 0],
                              opacity: [0, 1, 0],
                            }}
                            transition={{
                              duration: 2,
                              delay: i * 0.3,
                              repeat: Infinity,
                              ease: "easeOut"
                            }}
                          />
                        ))}
                      </div>
                    </Card>
                  </motion.div>
                )
              })}
            </div>

            {/* Instructions */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center mt-12"
            >
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                👆 Tap your name to start your chore adventure!
              </p>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 text-center">
          <p className="text-sm text-gray-400 dark:text-gray-500">
            Need help? Ask a parent to exit kiosk mode.
          </p>
        </div>
      </div>
    </div>
  )
}

export default ChildSelectionScreen